import { ChangeDetectorRef, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BrandNames } from 'sharedEnums/brand-names';
import { DynamicPageData } from 'sharedModels/dynamic-page-data';
import { AppConfigService } from 'sharedServices/app-config.service';
import { CmsSelectors } from 'sharedStores/cms/cms.module';
import { QuoteControllerSelectors } from 'sharedStores/quote-controller/quote-controller.module';
import { ModuleInfo } from 'sharedModels/module-info';
import { BaseDynamicPageComponent } from 'sharedGlobals/dynamic-component/base-dynamic-page.component';

@Directive()
@UntilDestroy()
export class QuoteDynamicPageComponent extends BaseDynamicPageComponent {
    brand: BrandNames;
    data$ = this.store$.select(QuoteControllerSelectors.selectActivePage);
    loading = this.store$.selectSignal(CmsSelectors.selectIsLoading);
    activePage: DynamicPageData;

    constructor(
        appConfig: AppConfigService,
        router: Router,
        store$: Store,
        changeRef: ChangeDetectorRef,
    ) {
        super(appConfig, router, store$, changeRef);
    }

    getModuleLoader: (moduleInfo: ModuleInfo) => Promise<any> = (moduleInfo: ModuleInfo) => {
        return Promise.resolve(undefined);
    };

    /**
     * Component Map for Standalone components, any components added here can be loaded entirely on their own without
     * the need to add them into a module
     * Note: We do this ideally only for entry points where we want the load time as small as possible, otherwise loading
     * a whole module at a time is the preferred method of attack
     */
    componentMap: Record<string, () => Promise<any>> = {
        ['CarMakePageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/car-details/pages/car-make-page/car-make-page.component'
            ).then((m) => m.CarMakePageComponent),
        ['CarPrefillPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/car-details/pages/car-prefill-page/car-prefill-page.component'
            ).then((m) => m.CarPrefillPageComponent),
        ['CarYearPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/car-details/pages/car-year-page/car-year-page.component'
            ).then((m) => m.CarYearPageComponent),
        ['CarColourPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/car-details/pages/car-colour-page/car-colour-page.component'
            ).then((m) => m.CarColourPageComponent),
        ['CarModelPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/car-details/pages/car-model-page/car-model-page.component'
            ).then((m) => m.CarModelPageComponent),
        ['CarTypePageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/car-details/pages/car-type-page/car-type-page.component'
            ).then((m) => m.CarTypePageComponent),
        ['QuoteStartPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/start/pages/quote-start-page/quote-start-page.component'
            ).then((m) => m.QuoteStartPageComponent),
        ['ContactDetailsPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/start/pages/contact-details-page/contact-details-page.component'
            ).then((m) => m.ContactDetailsPageComponent),
        ['HomeContentsAreasPageComponent']: () =>
            import(
                '../../../../../../quote/projects/base/modules/home-content-details/pages/areas-page/home-contents-areas-page.component'
            ).then((m) => m.HomeContentsAreasPageComponent),
    };

    /**
     * Asynchronously loads a standalone component based on the provided module information.
     *
     * @param {ModuleInfo} moduleInfo - The information about the module, including the name of the standalone component to load.
     * @returns {Promise<any>} A promise that resolves to the loaded component, or `undefined` if the component is not found in the `componentMap`.
     *
     * Usage:
     * This method retrieves the corresponding import function from the `componentMap` using the `standaloneComponent` property
     * of the `moduleInfo` object. If the import function exists, it is invoked to dynamically load the component.
     */
    getStandaloneComponent = async (moduleInfo: ModuleInfo): Promise<any> => {
        const importFunction = this.componentMap[moduleInfo.standaloneComponent];
        return importFunction ? await importFunction() : undefined;
    };
}
